import type {
  DataEditorRef,
  GridSelection,
  Item,
} from "@glideapps/glide-data-grid";
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useEffect,
  useRef,
} from "react";
import type { AdaptedSpreadsheetSheet } from "./adapter";
import { scheduleGridRowScroll } from "./grid-scroll";
import { findDisplayedSpreadsheetRow } from "./spreadsheet-region-view";
import type { ViewerUiState } from "./use-viewer-ui-state";
import { emptySelection } from "./utils";

interface UsePendingCellNavigationParams {
  activeSheetId: string | undefined;
  activeSheetValue: string;
  adaptedSheet: AdaptedSpreadsheetSheet | null;
  hasActiveRegionView: boolean;
  regionRowMap: number[];
  gridWrapperRef: RefObject<HTMLDivElement | null>;
  ui: Pick<
    ViewerUiState,
    | "pendingCell"
    | "setPendingCell"
    | "setGridSelection"
    | "setRegionViewStatesBySheet"
  >;
}

export const usePendingCellNavigation = ({
  activeSheetId,
  activeSheetValue,
  adaptedSheet,
  hasActiveRegionView,
  regionRowMap,
  gridWrapperRef,
  ui: {
    pendingCell,
    setPendingCell,
    setGridSelection,
    setRegionViewStatesBySheet,
  },
}: UsePendingCellNavigationParams) => {
  const scrollCancelRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (
      !pendingCell ||
      activeSheetId !== pendingCell.sheetId ||
      !adaptedSheet
    ) {
      return;
    }

    const col = Math.max(0, pendingCell.col - 1);
    const savedRow = Math.max(0, pendingCell.row - 1);
    const projectedRow = findDisplayedSpreadsheetRow(regionRowMap, savedRow);
    if (hasActiveRegionView && projectedRow === null) {
      setRegionViewStatesBySheet((current) => ({
        ...current,
        [activeSheetValue]: {},
      }));
      return;
    }
    const row = projectedRow ?? savedRow;
    const cell: Item = [col, row];
    setGridSelection({
      ...emptySelection,
      current: {
        cell,
        range: { x: col, y: row, width: 1, height: 1 },
        rangeStack: [],
      },
    });
    setPendingCell(null);
    scrollCancelRef.current?.();
    scrollCancelRef.current = scheduleGridRowScroll({
      gridWrapper: gridWrapperRef.current,
      row,
      getRowHeight: adaptedSheet.getRowHeight,
    });
  }, [
    pendingCell,
    activeSheetId,
    activeSheetValue,
    adaptedSheet,
    hasActiveRegionView,
    regionRowMap,
  ]);

  useEffect(() => () => scrollCancelRef.current?.(), []);
};

interface UseSearchNavigationParams {
  searchNavigationTarget: { row: number; col: number } | null;
  clearGridViewport: () => void;
  gridRef: RefObject<DataEditorRef | null>;
  setGridSelection: Dispatch<SetStateAction<GridSelection>>;
}

export const useSearchNavigation = ({
  searchNavigationTarget,
  clearGridViewport,
  gridRef,
  setGridSelection,
}: UseSearchNavigationParams) => {
  useEffect(() => {
    if (!searchNavigationTarget) return;

    clearGridViewport();
    const matchingCell: Item = [
      searchNavigationTarget.col,
      searchNavigationTarget.row,
    ];
    setGridSelection({
      ...emptySelection,
      current: {
        cell: matchingCell,
        range: {
          x: matchingCell[0],
          y: matchingCell[1],
          width: 1,
          height: 1,
        },
        rangeStack: [],
      },
    });
    gridRef.current?.scrollTo(
      searchNavigationTarget.col,
      searchNavigationTarget.row,
      "both",
      40,
      40,
      { hAlign: "center", vAlign: "center" },
    );
  }, [clearGridViewport, searchNavigationTarget]);
};
